import { useCallback, useMemo } from "react"

import { useBillingSubscription } from "@/billing"
import { useMetricsList } from "@/metrics/catalog"
import {
  MetricModal,
  MetricTable,
  type MetricCatalogItem,
} from "@/metrics/configure"
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@coval/ui"

import { UnsavedChangesModal } from "@/components/shared/UnsavedChangesModal"
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges"

import { useConversationMetricRuleEditor } from "../hooks/useConversationMetricRuleEditor"
import { useConversationMetricRuleMetricPicker } from "../hooks/useConversationMetricRuleMetricPicker"
import { useConversationMetricsConfigurationState } from "../hooks/useConversationMetricsConfigurationState"
import { useConversationMetricsConfigureController } from "../hooks/useConversationMetricsConfigureController"
import { useConversationMetricsConfigurationTransport } from "../transport/hooks/useConversationMetricsConfigurationTransport"
import { findConversationMetric } from "../utils/conversationMetricIdentity"
import { ConversationMetricRuleForm } from "./ConversationMetricRuleForm"
import { ConversationMetricsConfigurationOverview } from "./ConversationMetricsConfigurationOverview"

interface ConversationMetricsSettingsPanelProps {
  onBack?: () => void
  view?: "all" | "default" | "conditional"
}

export function ConversationMetricsSettingsPanel({
  onBack,
  view = "all",
}: ConversationMetricsSettingsPanelProps) {
  const {
    workspaceSettings,
    isError: isWorkspaceSettingsError,
    isLoading: isWorkspaceSettingsLoading,
    retrySettings,
    updateSettings,
    isUpdatingSettings,
    invalidateSettings,
  } = useConversationMetricsConfigurationTransport()
  const isWorkspaceSettingsUnavailable =
    isWorkspaceSettingsLoading || isWorkspaceSettingsError || !workspaceSettings

  const {
    data: metricsData,
    isLoading: isMetricsLoading,
    isError: isMetricsError,
    refetch: retryMetrics,
  } = useMetricsList()

  const metricsArray = useMemo(() => {
    return metricsData ? Object.values(metricsData) : []
  }, [metricsData])

  const {
    beginSavingChanges,
    closeConfigureMetricsModal,
    finishSavingChanges,
    handleCancelMetrics,
    handleDiscardChanges,
    handleRemoveMetric,
    handleToggleMetric,
    hasUnsavedChanges,
    isConfigureMetricsModalOpen,
    isSavingChanges,
    markAllChangesSaved,
    openConfigureMetricsModal,
    setTempRules,
    tempRules,
    tempSelectedMetrics,
  } = useConversationMetricsConfigurationState(workspaceSettings, metricsArray)
  const {
    addMetadataCondition,
    addStringArrayMetadataConditionValue,
    canSubmitRule,
    handleAddRuleModalClose,
    handleAddRuleModalOpen,
    handleCloseEditModal,
    handleCopyRule,
    handleDeleteRule,
    handleEditRule,
    handleMetadataConditionBooleanValueChange,
    handleMetadataConditionTypeChange,
    handleSaveRule,
    handleToggleRuleMetric,
    handleUpdateRule,
    isAddRuleModalOpen,
    isEditRuleModalOpen,
    metadataConditions,
    removeMetadataCondition,
    removeStringArrayMetadataConditionValue,
    selectedMetrics,
    setMetadataConditionKey,
    setMetadataConditionMatchType,
    setMetadataConditionValue,
    setSelectedMetrics,
    updateStringArrayMetadataConditionValue,
  } = useConversationMetricRuleEditor({
    rules: tempRules,
    setRules: setTempRules,
  })

  const {
    closeRuleMetricPicker,
    handleApplyRuleMetrics,
    handleCancelRuleMetrics,
    handleToggleTempRuleMetric,
    isRuleMetricPickerOpen,
    openRuleMetricPicker,
    tempRuleMetrics,
  } = useConversationMetricRuleMetricPicker({
    selectedMetrics,
    setSelectedMetrics,
  })

  const isSaving = isSavingChanges || isUpdatingSettings

  // Uploads exceeding the plan's default-plus-conditional metric limit are rejected, while a null limit is unlimited.
  const { data: billingSubscription } = useBillingSubscription()
  const metricsPerCallLimit =
    billingSubscription?.quota?.metrics_per_call_limit ?? null

  const selectedDefaultMetricNames = useMemo(
    () =>
      tempSelectedMetrics
        .map((identifier) => findConversationMetric(identifier, metricsArray))
        .map(
          (metric, index) => metric?.metric_name ?? tempSelectedMetrics[index],
        ),
    [metricsArray, tempSelectedMetrics],
  )

  const handleToggleDefaultMetric = useCallback(
    (metricName: string, isSelected: boolean) => {
      const metric = metricsArray.find(
        (item) => item.metric_name === metricName,
      )
      handleToggleMetric(metric?.id ?? metricName, isSelected)
    },
    [handleToggleMetric, metricsArray],
  )

  const { handleSaveChanges } = useConversationMetricsConfigureController({
    metrics: metricsArray,
    tempSelectedMetrics,
    tempRules,
    saveScope: view,
    isSaving,
    beginSavingChanges,
    finishSavingChanges,
    markAllChangesSaved,
    updateSettings,
    invalidateSettings,
  })

  const {
    isModalOpen: isUnsavedChangesModalOpen,
    isSaving: isModalSaving,
    error: saveError,
    handleConfirmSave,
    handleConfirmDiscard,
    handleConfirmCancel,
    handleNavigationAttempt,
  } = useUnsavedChanges({
    hasUnsavedChanges,
    isNavigationLocked: isSaving,
    onSave: handleSaveChanges,
    onDiscard: handleDiscardChanges,
    customNavigation: onBack,
  })

  const handleSaveMetrics = () => {
    closeConfigureMetricsModal()
  }

  const ruleFormFields = (
    <ConversationMetricRuleForm
      metricsByName={Object.fromEntries<MetricCatalogItem>([
        ...metricsArray.map((metric) => [metric.metric_name, metric] as const),
        ...metricsArray.map((metric) => [metric.id, metric] as const),
      ])}
      selectedMetrics={selectedMetrics}
      metadataConditions={metadataConditions}
      isMetricsLoading={isMetricsLoading}
      showConditionsWhenNoMetrics={isEditRuleModalOpen}
      onRemoveMetric={(metricName) => handleToggleRuleMetric(metricName, false)}
      onBrowseMetrics={openRuleMetricPicker}
      onAddMetadataCondition={addMetadataCondition}
      onRemoveMetadataCondition={removeMetadataCondition}
      onSetMetadataConditionKey={setMetadataConditionKey}
      onSetMetadataConditionType={handleMetadataConditionTypeChange}
      onSetMetadataConditionMatchType={setMetadataConditionMatchType}
      onSetMetadataConditionValue={setMetadataConditionValue}
      onSetBooleanConditionValue={handleMetadataConditionBooleanValueChange}
      onAddArrayConditionValue={addStringArrayMetadataConditionValue}
      onUpdateArrayConditionValue={updateStringArrayMetadataConditionValue}
      onRemoveArrayConditionValue={removeStringArrayMetadataConditionValue}
    />
  )

  const ruleMetricPickerFields = (
    <div className="h-full min-h-0">
      <MetricTable
        className="h-full"
        metrics={metricsArray}
        selectedMetrics={tempRuleMetrics.map(
          (identifier) =>
            findConversationMetric(identifier, metricsArray)?.metric_name ??
            identifier,
        )}
        onToggleMetric={(metricName, isSelected) => {
          const metric = metricsArray.find(
            (item) => item.metric_name === metricName,
          )
          handleToggleTempRuleMetric(metric?.id ?? metricName, isSelected)
        }}
        instanceId="rule-metrics"
      />
    </div>
  )

  const handleCloseAddRuleDialog = () => {
    closeRuleMetricPicker()
    handleAddRuleModalClose()
  }

  const handleCloseEditRuleDialog = () => {
    closeRuleMetricPicker()
    handleCloseEditModal()
  }

  const ruleDialogContentClassName = isRuleMetricPickerOpen
    ? "h-[85vh] !w-[92vw] !max-w-[1100px] overflow-hidden"
    : "max-h-[85vh] w-full !max-w-2xl overflow-hidden"
  const ruleDialogBodyClassName = isRuleMetricPickerOpen
    ? "min-h-0 overflow-hidden"
    : "min-h-0"

  return (
    <div data-testid="conversation-metrics-config-root" className="space-y-6">
      {(isWorkspaceSettingsError || isMetricsError) && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-coval-danger-border bg-coval-danger-surface p-3 text-sm text-coval-danger-on-surface">
          <span>
            {isWorkspaceSettingsError
              ? "Workspace metric settings could not be loaded. Retry before editing default metrics or rules."
              : "Metrics could not be loaded. Retry before saving default metrics or rules."}
          </span>
          <Button
            type="button"
            variant="secondary"
            className="shrink-0"
            onClick={() => {
              if (isWorkspaceSettingsError) {
                void retrySettings()
              }
              if (isMetricsError) {
                void retryMetrics()
              }
            }}
          >
            Retry
          </Button>
        </div>
      )}

      <ConversationMetricsConfigurationOverview
        view={view}
        metricsByName={metricsData}
        defaultMetricIds={tempSelectedMetrics}
        rules={tempRules}
        isConfigurationUnavailable={isWorkspaceSettingsUnavailable}
        metricsPerCallLimit={metricsPerCallLimit}
        onEditDefaultMetrics={openConfigureMetricsModal}
        onRemoveDefaultMetric={handleRemoveMetric}
        onAddRule={handleAddRuleModalOpen}
        onEditRule={handleEditRule}
        onCopyRule={handleCopyRule}
        onDeleteRule={handleDeleteRule}
      />

      <div className="flex justify-end gap-3 pt-2">
        {onBack && (
          <Button
            variant="secondary"
            disabled={isSaving}
            onClick={() => {
              handleNavigationAttempt()
            }}
          >
            Back
          </Button>
        )}
        <Button
          variant="default"
          disabled={
            isSaving ||
            isWorkspaceSettingsUnavailable ||
            isMetricsLoading ||
            isMetricsError
          }
          onClick={() => {
            void handleSaveChanges().catch(() => undefined)
          }}
        >
          Save Changes
        </Button>
      </div>

      <MetricModal
        isOpen={isConfigureMetricsModalOpen}
        onClose={handleCancelMetrics}
        metrics={metricsArray}
        selectedMetrics={selectedDefaultMetricNames}
        onApply={handleSaveMetrics}
        onToggleMetric={handleToggleDefaultMetric}
        instanceId="conversation-metrics-settings"
      />

      <Dialog
        open={isAddRuleModalOpen}
        onOpenChange={(open) => !open && handleCloseAddRuleDialog()}
      >
        <DialogContent className={ruleDialogContentClassName}>
          <DialogHeader>
            <DialogTitle>
              {isRuleMetricPickerOpen ? "Select Rule Metrics" : "Add Rule"}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className={ruleDialogBodyClassName}>
            {isRuleMetricPickerOpen ? ruleMetricPickerFields : ruleFormFields}
          </DialogBody>
          <DialogFooter>
            {isRuleMetricPickerOpen ? (
              <>
                <Button variant="secondary" onClick={handleCancelRuleMetrics}>
                  Cancel
                </Button>
                <Button variant="default" onClick={handleApplyRuleMetrics}>
                  Apply Selection
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" onClick={handleCloseAddRuleDialog}>
                  Cancel
                </Button>
                <Button
                  variant="default"
                  disabled={!canSubmitRule}
                  onClick={handleSaveRule}
                >
                  Add Rule
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isEditRuleModalOpen}
        onOpenChange={(open) => !open && handleCloseEditRuleDialog()}
      >
        <DialogContent className={ruleDialogContentClassName}>
          <DialogHeader>
            <DialogTitle>
              {isRuleMetricPickerOpen ? "Select Rule Metrics" : "Edit Rule"}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className={ruleDialogBodyClassName}>
            {isRuleMetricPickerOpen ? ruleMetricPickerFields : ruleFormFields}
          </DialogBody>
          <DialogFooter>
            {isRuleMetricPickerOpen ? (
              <>
                <Button variant="secondary" onClick={handleCancelRuleMetrics}>
                  Cancel
                </Button>
                <Button variant="default" onClick={handleApplyRuleMetrics}>
                  Apply Selection
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" onClick={handleCloseEditRuleDialog}>
                  Cancel
                </Button>
                <Button
                  variant="default"
                  disabled={!canSubmitRule}
                  onClick={handleUpdateRule}
                >
                  Update Rule
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UnsavedChangesModal
        isOpen={isUnsavedChangesModalOpen}
        onClose={handleConfirmCancel}
        onDiscard={handleConfirmDiscard}
        onSave={() => {
          void handleConfirmSave()
        }}
        isSaving={isModalSaving}
        error={saveError}
      />
    </div>
  )
}
